<template>
  <CustomModal
    id="definition-of-done-modal"
    modalClass="definition-of-done-modal"
    :clickToClose="false"
  >
    <template #header>
      <BaseHeading
        level="3"
        className="definition-of-done-modal__heading"
      >
        {{ config.taskTypeLabel }} Tasks Remaining
      </BaseHeading>
    </template>
    <template #body>
      <div class="definition-of-done-modal__content">
        <BaseSubHeading
          size="large"
          weight="400"
          className="definition-of-done-modal__intro"
        >
          {{ intro }}
        </BaseSubHeading>
        <ul class="definition-of-done-modal__tasks custom-scroll">
          <li
            v-for="task in config.tasks"
            :key="task.id"
            class="definition-of-done-modal__task"
          >
            <BaseSubHeading
              size="medium"
              weight="400"
              className="definition-of-done-modal__order"
            >
              {{ task.order }}
            </BaseSubHeading>
            <BaseSubHeading
              size="medium"
              weight="400"
              className="definition-of-done-modal__field definition-of-done-modal__name"
            >
              <span :title="task.name">{{ task.name }}</span>
              <span
                v-if="!task.checklistFullyChecked"
                class="definition-of-done-modal__checklist"
                title="Unchecked checklist items"
                aria-label="Unchecked checklist items"
              >
                <icon
                  data="@icon/task-check-list.svg"
                  width="var(--icon-24)"
                  height="var(--icon-24)"
                />
              </span>
            </BaseSubHeading>
            <BaseSubHeading
              size="medium"
              weight="400"
              className="definition-of-done-modal__field definition-of-done-modal__type"
            >
              {{ task.typeParsed.label }}
            </BaseSubHeading>
            <BaseSubHeading
              size="medium"
              weight="400"
              className="definition-of-done-modal__field definition-of-done-modal__hours"
            >
              {{ task.estimatedHours ?? "--" }}
            </BaseSubHeading>
            <Avatar
              class="definition-of-done-modal__avatar"
              :class="{ 'avatar-select__gap': !task.owner }"
              :src="task.owner?.pictureUrl ?? task.owner?.picture?.pictureUrl ?? null"
              :userEnabled="task.owner ? task.owner.enabled : true"
              :tooltipText="task.owner?.username"
              :gap="task.owner?.getInitials?.()"
              size="md"
              type="avatarGroup"
            />
          </li>
        </ul>
        <BaseSubHeading
          size="large"
          weight="400"
          className="definition-of-done-modal__summary"
        >
          Click “Confirm” to update remaining Tasks to 0 hrs.
        </BaseSubHeading>
      </div>
    </template>
    <template #footer="{ hide }">
      <ControlButtons
        @save="confirm"
        @cancel="hide"
      >
        <template #save>Confirm</template>
      </ControlButtons>
    </template>
  </CustomModal>
</template>

<script setup lang="ts">
import { computed } from "vue"

import Avatar from "@/components/Avatar/index.vue"
import ControlButtons from "@/components/ControlButtons/index.vue"
import CustomModal from "@/components/Modal/index.vue"

import type { Task } from "@/models"

/** Shows Tasks with remaining or unestimated hours while Confirm completes every related checklist. */
const props = defineProps<{
  config: {
    definitionLabel: string
    taskTypeLabel: string
    tasks: Task[]
    relatedTaskIds: string[]
    relatedTaskCount: number
  }
}>()
const emit = defineEmits<{ confirm: [config: typeof props.config] }>()

const intro = computed(() => {
  const count = props.config.tasks.length
  if (!count)
    return `All ${props.config.relatedTaskCount} related Tasks already have 0 hrs remaining.`
  const tasksLabel = count === 1 ? "Task" : "Tasks"
  return `The following ${count} ${tasksLabel} ${count === 1 ? "has" : "have"} Time Left or incomplete checklists remaining, indicating they are not finished yet.`
})

const confirm = () => emit("confirm", props.config)
</script>
